// src/components/pat/PatIllustrativePathwaysPanel.jsx
// Illustrative, non-adjudicative epistemic pathways

import React, { useMemo } from "react";

function headingFor(pathway) {
  switch (pathway?.epistemic_domain) {
    case "recontextualisation":
      return "Conceptual framing";
    case "recalibration":
      return "Measurement and operationalisation";
    case "reintegration":
      return "Evidence integration";
    case "boundary_recognition":
      return "Model boundary and scope";
    default:
      return pathway?.title || pathway?.heading || "Illustrative pathway";
  }
}

function listToString(value) {
  if (!Array.isArray(value) || value.length === 0) return null;

  const cleaned = value
    .map((item) => {
      if (typeof item === "string") return item.trim();

      if (item && typeof item === "object") {
        return item.label || item.displayLabel || item.id || "";
      }

      return "";
    })
    .filter(Boolean);

  return cleaned.length ? cleaned.join(", ") : null;
}

function normaliseDoMethod(doPanel) {
  return typeof doPanel?.method === "string"
    ? doPanel.method.toLowerCase().trim()
    : "";
}

function hasMultipleIdentificationRoutes(doPanel) {
  if (Array.isArray(doPanel?.estimands) && doPanel.estimands.length > 1) {
    return true;
  }

  const badge = typeof doPanel?.badge === "string" ? doPanel.badge : "";
  return badge.toLowerCase().trim() === "multiple identification routes";
}

function pathwayStatusLabel(doPanel) {
  if (hasMultipleIdentificationRoutes(doPanel)) {
    return "Multiple identification routes remain active";
  }

  const method = normaliseDoMethod(doPanel);

  if (method === "collider_caution") return "Collider caution remains active";
  if (method === "role_edge_conflict")
    return "Role-edge conflict remains active";
  if (method === "frontdoor") return "Mediated structure remains structural";
  if (method === "identifiable_under_adjustment") {
    return "Adjustment interpretation remains conditional";
  }
  if (method === "direct") return "Direct structure remains structural";
  if (method === "unidentifiable")
    return "Unidentified structure remains active";

  return "Do-calculus boundary remains active";
}

function pathwayContextForMethod(doPanel) {
  if (hasMultipleIdentificationRoutes(doPanel)) {
    return "The do-calculus layer identified more than one structural route. The pathways below do not merge these routes or resolve their differences; they support conceptual, measurement, evidentiary, and boundary review across the confirmed model.";
  }

  const method = normaliseDoMethod(doPanel);

  if (method === "collider_caution") {
    return "The collider / adjustment caution identified above remains in force. The pathways below do not authorise adjustment, intervention, or causal estimation. They help inspect what the current DAG may compress, omit, or operationalise too narrowly.";
  }

  if (method === "role_edge_conflict") {
    return "The role-edge conflict identified above remains in force. The pathways below do not resolve the mismatch; they support review of whether the graph, role assignment, or modelling frame should be revised.";
  }

  if (method === "frontdoor") {
    return "The mediated/pathway interpretation above remains a structural graph result. The pathways below extend that structure into conceptual, measurement, evidentiary, and boundary questions without converting it into empirical proof of mediation, effect magnitude, or real-world feasibility.";
  }

  if (method === "identifiable_under_adjustment") {
    return "The adjustment-based interpretation above remains conditional on the confirmed DAG and modelling assumptions. The pathways below broaden review of how those assumptions, measurements, and evidentiary boundaries might be questioned or refined.";
  }

  if (method === "direct") {
    return "The direct-identification result above remains a structural graph result. The pathways below broaden review of how the direct relation is framed, measured, evidenced, and bounded.";
  }

  if (method === "unidentifiable") {
    return "The current graph does not support a uniquely identified intervention effect. The pathways below remain valuable because they help inspect conditions that may guide conceptual clarification, measurement refinement, or graph revision.";
  }

  return "The pathways below preserve the do-calculus result while opening conceptual, methodological, evidentiary, and boundary questions for further review.";
}

function generateFallbackNarrative(pathway) {
  const roleCommitments = pathway?.role_commitments || {};

  const predictors = listToString(roleCommitments.predictors);
  const mediators = listToString(roleCommitments.mediators);
  const confounders = listToString(roleCommitments.confounders);
  const outcome = roleCommitments.outcome || "the outcome";
  const bcsmLabel = roleCommitments.bcsm_label || "undetermined stability";

  switch (pathway?.epistemic_domain) {
    case "recontextualisation":
      return `This pathway recontextualises how ${
        predictors || "the nominated candidate X variable"
      }, ${mediators || "the nominated mediators"}, ${
        confounders || "the nominated confounder/background variables"
      }, and ${outcome} are being understood within and beyond the confirmed DAG. It asks whether the current graph fixes the most useful conceptual frame, or whether another frame would expose assumptions the DAG currently compresses.`;

    case "recalibration":
      return `This pathway treats the confirmed model as methodologically conditional, asking how its present adequacy may depend on measurement choices, inferential framing, operational thresholds, and recalibration conditions. Current stability is read as ${bcsmLabel}, but that stability does not settle whether the variables have been measured or operationalised in the most informative way.`;

    case "reintegration":
      return `This pathway reads the confirmed model as a site of reintegration across distinct but credible explanatory or evaluative perspectives organised around ${outcome}. It asks how different warrant domains might be coordinated without collapsing them into a single explanatory frame.`;

    case "boundary_recognition":
      return `This pathway marks the model boundary by clarifying what the present formulation includes, excludes, holds fixed, or cannot register directly. It asks what remains outside the authorised graph but may still matter for interpretation, revision, or empirical follow-up.`;

    default:
      return "This pathway opens a philosophy-of-science reading of the confirmed model by asking what the current DAG may foreground, compress, exclude, or leave available for further conceptual revision.";
  }
}

function normalisePathways(pathways) {
  if (Array.isArray(pathways)) return pathways;
  if (Array.isArray(pathways?.pathways)) return pathways.pathways;

  return [
    { epistemic_domain: "recontextualisation", role_commitments: {} },
    { epistemic_domain: "recalibration", role_commitments: {} },
    { epistemic_domain: "reintegration", role_commitments: {} },
    { epistemic_domain: "boundary_recognition", role_commitments: {} },
  ];
}

export default function PatIllustrativePathwaysPanel({ pathways, doPanel }) {
  const items = useMemo(() => normalisePathways(pathways), [pathways]);

  return (
    <div className="rounded-lg border border-slate-700/70 bg-slate-900/70 p-4">
      <h4 className="text-xl font-semibold text-amber-300 text-center mb-1">
        Post-DAG Illustrative Pathways
      </h4>

      <div className="text-xs text-slate-400 text-center mb-3">
        (Epistemic expansion beyond the bounded DAG)
      </div>

      <div className="rounded-md border border-slate-700/60 bg-slate-900/60 p-3 mb-4 space-y-2">
        <p className="text-[0.75rem] text-slate-300 leading-snug">
          The confirmed DAG defines the formal causal structure; however, it
          does not exhaust the inquiry. The pathways below support conceptual
          reframing, measurement critique, evidence integration, and
          model-boundary recognition.
        </p>

        <div className="rounded border border-amber-700/40 bg-amber-950/20 p-2">
          <div className="text-[0.72rem] font-semibold text-amber-200 mb-1">
            {pathwayStatusLabel(doPanel)}
          </div>

          <p className="text-[0.75rem] text-slate-400 leading-snug">
            {pathwayContextForMethod(doPanel)}
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {items.map((pathway, index) => {
          const description =
            typeof pathway?.description === "string" &&
            pathway.description.trim().length > 0
              ? pathway.description.trim()
              : null;

          const workedExample =
            typeof pathway?.worked_example === "string" &&
            pathway.worked_example.trim().length > 0
              ? pathway.worked_example.trim()
              : null;

          const hasNarrative = description || workedExample;

          return (
            <div
              key={`${pathway?.epistemic_domain || "pathway"}-${index}`}
              className="border border-slate-700/60 rounded p-3 bg-slate-950/20"
            >
              <div className="text-lg text-slate-200 font-semibold mb-1">
                {headingFor(pathway)}
              </div>

              {pathway?.epistemic_type && (
                <div className="text-sm text-slate-400 italic">
                  {pathway.epistemic_type}
                </div>
              )}

              {hasNarrative ? (
                <>
                  {description && (
                    <div className="text-sm text-slate-400 mt-1 leading-snug">
                      {description}
                    </div>
                  )}

                  {workedExample && (
                    <div className="text-sm text-slate-400 mt-2 leading-snug">
                      <span className="italic">Worked example:</span>{" "}
                      {workedExample}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-sm text-slate-400 mt-2 leading-snug">
                  {generateFallbackNarrative(pathway)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
